import React from 'react';
import { Phone, Mail, Globe, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Brand glyphs not shipped by lucide — minimal inline SVGs.
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...props}>
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.05-2.82h-3.11v12.4a2.53 2.53 0 1 1-2.53-2.53c.26 0 .51.04.75.11V9.8a5.64 5.64 0 0 0-.75-.05 5.62 5.62 0 1 0 5.62 5.62V8.9a7.36 7.36 0 0 0 4.31 1.38V7.16a4.28 4.28 0 0 1-3.24-1.34Z" />
  </svg>
);
const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...props}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24a8.2 8.2 0 0 1 5.82 2.42 8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.23 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43l-.48-.01c-.16 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.25 3.74.59.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
  </svg>
);

// Standalone company footer for business / shop pages — replaces the global
// KoLV footer + investment bar with the company's own contacts & requisites.
// Empty fields collapse gracefully so the grid never looks broken.
export const CompanyFooter = ({ theme = 'dark', name, logo, contacts = {}, requisites = {} }) => {
  const { language } = useLanguage();
  const dark = theme === 'dark';

  const t = (lv, en, ru) => (language === 'ru' ? ru : language === 'en' ? en : lv);

  const wrap = dark ? 'bg-[#050505] border-white/10 text-[#A3A3A3]' : 'bg-[#F5F5F5] border-black/5 text-[#525252]';
  const heading = dark ? 'text-white/50' : 'text-black/50';
  const strong = dark ? 'text-white' : 'text-[#171717]';
  const rowHover = dark ? 'hover:text-white' : 'hover:text-black';
  const body = dark ? 'font-manrope' : 'font-figtree';

  const normUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
  const waLink = (v) => {
    const s = (v || '').trim();
    if (!s) return null;
    if (/^https?:\/\//i.test(s)) return s;
    const digits = s.replace(/[^\d]/g, '');
    return digits ? `https://wa.me/${digits}` : null;
  };
  const cleanWeb = (u) => (u || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');

  const contactRows = [
    contacts.phone && { key: 'phone', icon: Phone, label: contacts.phone, href: `tel:${contacts.phone}`, tid: 'contact-phone' },
    contacts.email && { key: 'email', icon: Mail, label: contacts.email, href: `mailto:${contacts.email}`, tid: 'contact-email' },
    contacts.website && { key: 'web', icon: Globe, label: cleanWeb(contacts.website), href: normUrl(contacts.website), external: true, tid: 'contact-website' },
    contacts.address && { key: 'addr', icon: MapPin, label: contacts.address, tid: 'contact-address' },
  ].filter(Boolean);

  const reqRows = [
    requisites.legal_name && { label: t('Juridiskais nosaukums', 'Legal name', 'Юр. название'), value: requisites.legal_name, tid: 'req-legal-name' },
    requisites.registration_number && { label: t('Reģ. Nr.', 'Reg. No.', 'Рег. №'), value: requisites.registration_number, tid: 'req-reg-num' },
    requisites.vat_number && { label: t('PVN Nr.', 'VAT No.', 'НДС №'), value: requisites.vat_number, tid: 'req-vat-num' },
    requisites.legal_address && { label: t('Juridiskā adrese', 'Legal address', 'Юр. адрес'), value: requisites.legal_address, tid: 'req-legal-address' },
  ].filter(Boolean);

  const hasContacts = contactRows.length > 0;
  const hasReq = reqRows.length > 0;

  const socials = [
    contacts.facebook && { key: 'fb', label: 'Facebook', icon: Facebook, href: normUrl(contacts.facebook), tid: 'social-facebook' },
    contacts.instagram && { key: 'ig', label: 'Instagram', icon: Instagram, href: normUrl(contacts.instagram), tid: 'social-instagram' },
    contacts.tiktok && { key: 'tt', label: 'TikTok', icon: TikTokIcon, href: normUrl(contacts.tiktok), tid: 'social-tiktok' },
    contacts.youtube && { key: 'yt', label: 'YouTube', icon: Youtube, href: normUrl(contacts.youtube), tid: 'social-youtube' },
    contacts.whatsapp && waLink(contacts.whatsapp) && { key: 'wa', label: 'WhatsApp', icon: WhatsAppIcon, href: waLink(contacts.whatsapp), tid: 'social-whatsapp' },
  ].filter(Boolean);
  const socialBtn = dark
    ? 'border-white/15 text-white/70 hover:text-white hover:border-amber-500/60 hover:bg-amber-500/10'
    : 'border-black/10 text-black/60 hover:text-black hover:border-[#D97736]/60 hover:bg-[#D97736]/5';

  return (
    <footer className={`border-t ${wrap} ${body}`} data-testid="company-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {/* Brand */}
          <div data-testid="company-brand-col">
            <div className="flex items-center gap-3 mb-4">
              {logo && (
                <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 border border-current/10">
                  <img src={logo} alt={name} className="w-full h-full object-cover" />
                </div>
              )}
              <h3 className={`text-2xl font-bold font-outfit tracking-tight ${strong}`}>{name}</h3>
            </div>
            <p className="text-xs opacity-50 mt-6">
              {t('Darbina', 'Powered by', 'На платформе')}{' '}
              <a href="/" className={`font-semibold transition-colors duration-200 ${rowHover}`}>KoLV</a>
            </p>
            {socials.length > 0 && (
              <div className="flex items-center gap-2.5 mt-5" data-testid="company-socials">
                {socials.map((s) => {
                  const Icon = s.icon;
                  return (
                    <a
                      key={s.key}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-200 ${socialBtn}`}
                      data-testid={s.tid}
                      aria-label={s.label}
                    >
                      <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Contacts */}
          {hasContacts && (
            <div data-testid="company-contacts-col">
              <p className={`text-xs uppercase tracking-[0.2em] font-semibold mb-6 ${heading}`}>
                {t('Kontakti', 'Contacts', 'Контакты')}
              </p>
              <ul className="space-y-3.5">
                {contactRows.map((r) => {
                  const Icon = r.icon;
                  const inner = (
                    <span className="inline-flex items-start gap-3">
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${dark ? 'text-amber-500' : 'text-[#D97736]'}`} />
                      <span className="text-sm leading-relaxed">{r.label}</span>
                    </span>
                  );
                  return (
                    <li key={r.key} data-testid={r.tid}>
                      {r.href ? (
                        <a href={r.href} {...(r.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          className={`transition-colors duration-200 ${rowHover}`}>
                          {inner}
                        </a>
                      ) : inner}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Requisites */}
          {hasReq && (
            <div data-testid="company-requisites-col">
              <p className={`text-xs uppercase tracking-[0.2em] font-semibold mb-6 ${heading}`}>
                {t('Rekvizīti', 'Requisites', 'Реквизиты')}
              </p>
              <ul className="space-y-3.5">
                {reqRows.map((r) => (
                  <li key={r.tid} data-testid={r.tid}>
                    <p className="text-xs opacity-50">{r.label}</p>
                    <p className={`text-sm ${strong}`}>{r.value}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className={`mt-14 pt-6 border-t ${dark ? 'border-white/10' : 'border-black/5'} text-xs opacity-40`}>
          © {new Date().getFullYear()} {name}
        </div>
      </div>
    </footer>
  );
};

export default CompanyFooter;
